'use client';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Shield,
  Users,
  ChevronRight,
  Plus,
  Trash2,
  Crown,
  Eye,
  Edit,
  Settings,
  FileText,
} from 'lucide-react';
import { UserRolesList, UserRole } from './UserRolesList';

export interface RoleNode {
  name: string;
  description?: string;
  users: string[];
  children: string[];
  permissions?: string[];
}

export interface RoleHierarchy {
  [roleName: string]: RoleNode;
}

export interface RoleHierarchyTreeProps {
  hierarchy: RoleHierarchy;
  roleAssignments: UserRole[];
  onAddInheritance?: (childRole: string, parentRole: string) => void;
  onRemoveInheritance?: (childRole: string, parentRole: string) => void;
  onSelectUser?: (user: string) => void;
}

// Default role descriptions and icons
const roleMetadata: Record<string, { description: string; icon: React.ElementType; color: string }> = {
  admin: {
    description: 'Full system access with all permissions',
    icon: Crown,
    color: 'text-amber-600 bg-amber-50 border-amber-200'
  },
  manager: {
    description: 'Can manage team members and resources',
    icon: Users,
    color: 'text-purple-600 bg-purple-50 border-purple-200'
  },
  developer: {
    description: 'Can create, edit, and execute workflows',
    icon: Settings,
    color: 'text-blue-600 bg-blue-50 border-blue-200'
  },
  member: {
    description: 'Basic read and execute access',
    icon: FileText,
    color: 'text-green-600 bg-green-50 border-green-200'
  },
  viewer: {
    description: 'Read-only access to resources',
    icon: Eye,
    color: 'text-slate-600 bg-slate-50 border-slate-200'
  },
  billing: {
    description: 'Access to billing and payment information',
    icon: FileText,
    color: 'text-emerald-600 bg-emerald-50 border-emerald-200'
  }
};

export function RoleHierarchyTree({
  hierarchy,
  roleAssignments,
  onAddInheritance,
  onRemoveInheritance,
  onSelectUser
}: RoleHierarchyTreeProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState<string>('');
  const [selectedParent, setSelectedParent] = useState<string>('');

  // Build hierarchy from role assignments
  const roles = Object.keys(hierarchy).length > 0
    ? Object.keys(hierarchy)
    : [...new Set(roleAssignments.map(a => a.role))];

  // Get users for a specific role
  const getUsersForRole = (role: string): string[] => {
    if (hierarchy[role]?.users) {
      return hierarchy[role].users;
    }
    return roleAssignments.filter(a => a.role === role).map(a => a.user);
  };

  // Get child roles (roles that inherit from this role)
  const getChildRoles = (role: string): string[] => {
    return hierarchy[role]?.children || [];
  };

  // Root roles (roles with no parent)
  const rootRoles = roles.filter(role => {
    return !roles.some(other => getChildRoles(other).includes(role));
  });

  const handleAddInheritance = () => {
    if (selectedChild && selectedParent && onAddInheritance) {
      onAddInheritance(selectedChild, selectedParent);
      setAddDialogOpen(false);
      setSelectedChild('');
      setSelectedParent('');
    }
  };

  const RoleIcon = ({ role }: { role: string }) => {
    const meta = roleMetadata[role.toLowerCase()] || roleMetadata.member;
    const Icon = meta.icon;
    return <Icon className="h-4 w-4" />;
  };

  const renderRole = (role: string, depth: number = 0): React.ReactNode => {
    const users = getUsersForRole(role);
    const children = getChildRoles(role);
    const meta = roleMetadata[role.toLowerCase()] || roleMetadata.member;

    return (
      <AccordionItem key={role} value={role} className={`border-0 ${depth > 0 ? 'ml-6 border-l-2 border-slate-200 pl-4' : ''}`}>
        <AccordionTrigger className="hover:no-underline py-3 px-0">
          <div className="flex items-center gap-3 flex-1">
            <div className={`p-2 rounded-md border ${meta.color}`}>
              <RoleIcon role={role} />
            </div>
            <div className="text-left flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium capitalize">{role}</span>
                <Badge variant="secondary" className="text-xs">
                  {users.length} user{users.length !== 1 ? 's' : ''}
                </Badge>
                {children.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {children.length} inherited
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {hierarchy[role]?.description || meta.description}
              </p>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pt-2 pb-4">
          <div className="space-y-4">
            {/* Users Section */}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Users with this role
              </h4>
              {users.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {users.map(user => (
                    <Badge
                      key={user}
                      variant="outline"
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => onSelectUser?.(user)}
                    >
                      {user}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">No users assigned</p>
              )}
            </div>

            {/* Inherited Roles Section */}
            {children.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <ChevronRight className="h-4 w-4" />
                  Roles that inherit from {role}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {children.map(child => (
                    <div key={child} className="flex items-center gap-1">
                      <Badge variant="secondary" className="capitalize">
                        {child}
                      </Badge>
                      {onRemoveInheritance && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 text-slate-400 hover:text-red-500"
                          onClick={() => onRemoveInheritance(child, role)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Permissions Preview */}
            {hierarchy[role]?.permissions && hierarchy[role].permissions!.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Permissions
                </h4>
                <div className="flex flex-wrap gap-1">
                  {hierarchy[role].permissions!.map(perm => (
                    <Badge key={perm} variant="outline" className="text-xs font-mono">
                      {perm}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Nested Child Roles */}
            {children.length > 0 && (
              <Accordion type="multiple" className="mt-2">
                {children.map(child => renderRole(child, depth + 1))}
              </Accordion>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-indigo-600" />
              Role Hierarchy
            </CardTitle>
            <CardDescription>
              Visual representation of role relationships and inheritance
            </CardDescription>
          </div>
          {onAddInheritance && (
            <Button variant="outline" size="sm" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Inheritance
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {roles.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            No roles defined yet. Assign roles to users to see the hierarchy.
          </div>
        ) : (
          <Accordion type="multiple" defaultValue={rootRoles} className="space-y-2">
            {rootRoles.map(role => renderRole(role))}
          </Accordion>
        )}
      </CardContent>

      {/* Add Inheritance Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Role Inheritance</DialogTitle>
            <DialogDescription>
              Define a parent-child relationship between roles. The child role will inherit all permissions from the parent.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Child Role (inherits from)</label>
              <Select value={selectedChild} onValueChange={setSelectedChild}>
                <SelectTrigger>
                  <SelectValue placeholder="Select child role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.filter(r => r !== selectedParent).map(role => (
                    <SelectItem key={role} value={role}>
                      <span className="capitalize">{role}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-center">
              <ChevronRight className="h-5 w-5 text-slate-400 rotate-90" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Parent Role (provides permissions)</label>
              <Select value={selectedParent} onValueChange={setSelectedParent}>
                <SelectTrigger>
                  <SelectValue placeholder="Select parent role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.filter(r => r !== selectedChild).map(role => (
                    <SelectItem key={role} value={role}>
                      <span className="capitalize">{role}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddInheritance} disabled={!selectedChild || !selectedParent}>
              Add Inheritance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
